import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload, Trash2 } from 'lucide-react';
import { getProduct, uploadImages, updateProduct } from '../../utils/api';
import { useAuth } from '../../components/auth/AuthContext';

interface EditProductFormData {
  title: string;
  category: string;
  product_type: string;
  size: string;
  price: string;
  description: string;
  images: File[];
}

interface UploadedImage {
  url: string;
  original_name: string;
  saved_name: string;
}

const EditProduct = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [currentImages, setCurrentImages] = useState<string[]>([]);
  const [formData, setFormData] = useState<EditProductFormData>({
    title: '',
    category: '',
    product_type: 'Sonstiges',
    size: '',
    price: '',
    description: '',
    images: []
  });

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const loadProduct = async () => {
      try {
        if (!id) return;
        const response = await getProduct(id);
        if (response.success && response.data) {
          const product = response.data;
          setFormData({
            title: product.title,
            category: product.category,
            product_type: product.product_type || 'Sonstiges',
            size: product.size,
            price: product.price.toString(),
            description: product.description || '',
            images: []
          });
          const allImages = Array.from(new Set([
            product.image_url,
            ...(product.images || [])
          ])).filter(Boolean);
          setCurrentImages(allImages);
        } else {
          setError(response.message);
        }
      } catch (err) {
        setError('Fehler beim Laden des Produkts');
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [id, user, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      try {
        const uploadResult = await uploadImages(files, id ? parseInt(id) : undefined);
        if (uploadResult.success) {
          const newImageUrls = uploadResult.data.map((img: UploadedImage) => img.url);
          setCurrentImages(prevImages => Array.from(new Set([...prevImages, ...newImageUrls])));
        } else {
          setError(uploadResult.message);
        }
      } catch {
        setError('Fehler beim Hochladen der Bilder');
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setCurrentImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (!formData.title || !formData.price || !formData.category || !formData.size) {
        throw new Error('Bitte fülle alle erforderlichen Felder aus.');
      }
      if (currentImages.length === 0) {
        throw new Error('Mindestens ein Bild ist erforderlich.');
      }

      const price = parseFloat(formData.price);
      if (isNaN(price) || price <= 0) {
        throw new Error('Bitte gib einen gültigen Preis ein.');
      }

      const updateData = {
        product_id: Number(id),
        title: formData.title,
        description: formData.description,
        price,
        category: formData.category,
        product_type: formData.product_type,
        size: formData.size,
        image_url: currentImages[0],
        additional_images: currentImages.slice(1)
      };

      const response = await updateProduct(Number(id), updateData);
      if (response.success) {
        navigate('/my-products');
      } else {
        throw new Error(response.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein unbekannter Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <p className="text-center">Lade Produkt...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-2xl mx-auto bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Produkt bearbeiten</h1>

        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Aktuelle Bilder */}
          {currentImages.length > 0 && (
            <div className="mb-6">
              <label className="block text-gray-700 mb-2">Aktuelle Bilder</label>
              <div className="grid grid-cols-3 gap-4">
                {currentImages.map((imageUrl, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={`${window.location.origin}${imageUrl}`}
                      alt={`Bild ${index + 1}`}
                      className="w-full h-32 object-cover rounded"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 size={16} />
                    </button>
                    {index === 0 && (
                      <div className="absolute bottom-2 left-2 bg-black/50 text-white px-2 py-1 text-xs rounded">
                        Hauptbild
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Neue Bilder Upload */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2">Neue Bilder hochladen</label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                id="images"
                onChange={handleImageUpload}
              />
              <label
                htmlFor="images"
                className="flex flex-col items-center justify-center cursor-pointer"
              >
                <Upload size={32} className="text-gray-400 mb-2" />
                <span className="text-gray-500">Klicke zum Hochladen oder ziehe Bilder hierher</span>
              </label>
            </div>
          </div>

          {/* Titel */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="title">Titel</label>
            <input
              type="text"
              id="title"
              name="title"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Kategorie */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="category">Kategorie</label>
            <select
              id="category"
              name="category"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.category}
              onChange={handleInputChange}
              required
            >
              <option value="">Kategorie wählen</option>
              <option value="Männer">Männer</option>
              <option value="Frauen">Frauen</option>
              <option value="Jungen">Jungen</option>
              <option value="Mädchen">Mädchen</option>
              <option value="Equipment">Equipment</option>
            </select>
          </div>

          {/* Produktart */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="product_type">Produktart</label>
            <select
              id="product_type"
              name="product_type"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.product_type}
              onChange={handleInputChange}
            >
              <option value="Sonstiges">Sonstiges</option>
              <option value="Schuhe">Schuhe</option>
              <option value="Trikot">Trikot / Jersey</option>
              <option value="Hose">Hose / Short</option>
              <option value="Pullover">Pullover / Hoodie</option>
              <option value="Jacke">Jacke</option>
              <option value="Tasche">Tasche / Rucksack</option>
              <option value="Ball">Ball</option>
            </select>
          </div>

          {/* Größe */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="size">Größe</label>
            <input
              type="text"
              id="size"
              name="size"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.size}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Preis */}
          <div className="mb-4">
            <label className="block text-gray-700 mb-2" htmlFor="price">Preis (€)</label>
            <input
              type="number"
              step="0.01"
              id="price"
              name="price"
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.price}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Beschreibung */}
          <div className="mb-6">
            <label className="block text-gray-700 mb-2" htmlFor="description">Beschreibung</label>
            <textarea
              id="description"
              name="description"
              rows={4}
              className="w-full p-3 border border-gray-300 rounded focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              value={formData.description}
              onChange={handleInputChange}
            />
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/my-products')}
              className="flex-1 bg-gray-100 text-gray-700 py-3 px-4 rounded hover:bg-gray-200 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-orange-500 text-white py-3 px-4 rounded hover:bg-orange-600 transition-colors disabled:opacity-50"
            >
              {isLoading ? 'Wird gespeichert...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProduct;
